import { Notice, Skeleton, SkeletonTypes, Text, HStack, Stack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React from 'react';

import { ProductsBar, ProductsBarTypes } from '../ProductsBar';

export declare namespace ProductDistributionTypes {
  interface InfoProps {
    label: string;
    value?: string | null;
  }

  interface BaseProps extends ProductsBarTypes.Props {
    loading: boolean;
    isMobile?: boolean;
  }

  interface LoadingProps extends ProductsBarTypes.Props, BaseProps {
    loading: true;
    mainInfo?: InfoProps;
    secondaryInfo?: InfoProps[];
    alertText?: string;
  }

  interface CommonProps extends ProductsBarTypes.Props, BaseProps {
    loading: false;
    mainInfo: InfoProps;
    secondaryInfo: InfoProps[];
    alertText: string;
  }

  type Props = CommonProps | LoadingProps;
}

const ColoredSkeleton = (props: SkeletonTypes.Props) => (
  <Skeleton {...props} endColor="brand.primary.lightest" />
);

export const ProductDistribution = ({
  loading,
  mainInfo,
  isMobile,
  secondaryInfo,
  products,
  alertText,
  activeDataKey,
  setActiveDataKey,
}: ProductDistributionTypes.Props) => {
  const renderInfos = () => {
    if (isMobile) {
      const mobileInfos = [mainInfo, ...(secondaryInfo || [])];
      return (
        <VStack space={Spacing['stack.nano']}>
          {mobileInfos.map((info) => (
            <HStack
              key={info?.label}
              space={Spacing['stack.quarck']}
              justifyContent="space-between"
              alignItems="center">
              <Text
                color="neutral.medium"
                variant="semibold"
                textTransform="uppercase"
                fontSize={Typography['fontsize.xs']}>
                {info?.label as string}
              </Text>
              <Text
                color={'neutral.black'}
                variant="semibold"
                textTransform="uppercase"
                fontSize={Typography['fontsize.xs']}
                textAlign="right">
                {info?.value as string}
              </Text>
            </HStack>
          ))}
        </VStack>
      );
    }

    return (
      <HStack
        space={Utils.Responsive.TabletDesktop({
          desktop: Spacing['inline.lg'],
          tablet: 0,
        })}
        alignItems={'center'}
        justifyContent={Utils.Responsive.TabletDesktop({
          desktop: 'inherit',
          tablet: 'space-between',
        })}>
        <VStack space={Spacing['stack.quarck']}>
          <Text variant="semibold" fontSize={Typography['fontsize.xl']} textTransform="uppercase">
            {mainInfo?.value as string}
          </Text>
          <Text variant="semibold" color="neutral.medium" textTransform="uppercase">
            {mainInfo?.label as string}
          </Text>
        </VStack>
        {secondaryInfo?.map((info) => (
          <VStack
            key={info.label}
            space={Spacing['stack.quarck']}
            justifyContent={'space-around'}
            height={'full'}>
            <Text variant="semibold" color="neutral.medium" textTransform="uppercase">
              {info.label}
            </Text>
            <Text variant="semibold" color="neutral.black" textTransform="uppercase">
              {info.value || ''}
            </Text>
          </VStack>
        ))}
      </HStack>
    );
  };

  if (loading) {
    return (
      <VStack
        space={Spacing['stack.xxs']}
        width={'full'}
        direction={Utils.Responsive.DesktopPhone({ desktop: 'column', phone: 'column-reverse' })}>
        <Stack
          space={Utils.Responsive.DesktopPhone({
            desktop: Spacing['inline.lg'],
            phone: Spacing['stack.nano'],
          })}
          alignItems={Utils.Responsive.DesktopPhone({ desktop: 'center', phone: 'inherit' })}
          direction={Utils.Responsive.DesktopPhone({ desktop: 'row', phone: 'column' })}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Stack
              key={index.toString()}
              space={Spacing['stack.nano']}
              justifyContent={Utils.Responsive.DesktopPhone({
                desktop: 'inherit',
                phone: 'space-between',
              })}
              direction={Utils.Responsive.DesktopPhone({ desktop: 'column', phone: 'row' })}>
              {Array.from({ length: 2 }).map((__, n) => (
                <ColoredSkeleton
                  key={n.toString()}
                  rounded={'full'}
                  width={Utils.Responsive.DesktopPhone({
                    desktop: 100,
                    phone: n ? '18%' : '45%',
                  })}
                  height={Utils.Responsive.DesktopPhone({
                    desktop: '10px',
                    phone: '14px',
                  })}
                />
              ))}
            </Stack>
          ))}
        </Stack>
        <Stack
          direction={Utils.Responsive.TabletDesktop({
            desktop: 'column-reverse',
            tablet: 'column',
          })}
          space={Spacing['stack.xxs']}>
          <ColoredSkeleton rounded={'full'} width={'100%'} height={14} />
          {!isMobile && (
            <HStack flexWrap="wrap" justifyContent="space-between" alignItems={'center'}>
              {Array.from({ length: 8 }).map((_, n) => (
                <ColoredSkeleton
                  key={n.toString()}
                  rounded={'full'}
                  width={`${100 / 9}%`}
                  height={14}
                />
              ))}
            </HStack>
          )}
        </Stack>
      </VStack>
    );
  }

  return (
    <VStack
      width={'full'}
      space={Spacing['stack.xxs']}
      direction={Utils.Responsive.PhoneTablet({
        tablet: 'column',
        phone: 'column-reverse',
      })}>
      <Stack
        direction={Utils.Responsive.All({
          desktop: 'row',
          tablet: 'column',
          phone: 'column-reverse',
        })}>
        {renderInfos()}
        <Stack
          flex={1}
          alignSelf="flex-start"
          alignItems={Utils.Responsive.TabletDesktop({ desktop: 'end', tablet: 'stretch' })}
          width={Utils.Responsive.TabletDesktop({ desktop: '80%', tablet: '100%' })}
          ml={Utils.Responsive.TabletDesktop({ desktop: Spacing['inline.nano'], tablet: 0 })}
          mt={Utils.Responsive.All({ desktop: 0, tablet: Spacing['inline.xxs'], phone: 0 })}
          mb={Utils.Responsive.PhoneTablet({ tablet: 0, phone: Spacing['inline.xxxs'] })}>
          <Notice
            testID="data-info-notice"
            type="alert"
            fontSize={isMobile ? Typography['fontsize.xs'] : Typography['fontsize.md']}
            textObjects={[{ text: alertText, variant: 'regular' }]}
          />
        </Stack>
      </Stack>
      <ProductsBar
        products={products}
        isMobile={isMobile}
        activeDataKey={activeDataKey}
        setActiveDataKey={setActiveDataKey}
      />
    </VStack>
  );
};

ProductDistribution.displayName = 'ProductDistribution';
